import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Aggregates dated events from every domain into a single array, used by
 * both the month-view density dots and the day detail page.
 *
 * v1: pulls Sessions, Meals, BodyMeasurement, SupplementIntake, RecoverySession,
 * SubjectiveCheckIn, and Project end_date deadlines for a given range.
 * Threads / external calendar / MealSlot DayPlan land alongside their
 * data sources.
 */
export type CalendarKind =
  | "user_event"
  | "external"
  | "session"
  | "meal"
  | "body"
  | "supplement"
  | "recovery"
  | "mood"
  | "project_deadline";

export type CalendarEvent = {
  id: string;
  date: string; // yyyy-mm-dd
  kind: CalendarKind;
  title: string;
  detail?: string;
  ts?: string;
};

export function useCalendarEvents(rangeStart: Date, rangeEnd: Date) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery<CalendarEvent[]>({
    queryKey: [
      "calendar_events",
      userId,
      rangeStart.toISOString().slice(0, 10),
      rangeEnd.toISOString().slice(0, 10),
    ],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const startIso = rangeStart.toISOString();
      const endIso = rangeEnd.toISOString();
      const startDate = isoDate(rangeStart);
      const endDate = isoDate(rangeEnd);

      const [usr, ext, sess, meals, body, supp, rec, sub, proj] = await Promise.all([
        // calendar_event added in BW10 — typed loosely until types regen
        (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> })
          .from("calendar_event")
          .select("id, starts_at, ends_at, title, location, all_day, source")
          .eq("user_id", userId)
          .gte("starts_at", startIso)
          .lt("starts_at", endIso),
        supabase
          .from("external_calendar_event")
          .select("id, source, source_id, starts_at, ends_at, title, location, all_day")
          .eq("user_id", userId)
          .gte("starts_at", startIso)
          .lt("starts_at", endIso),
        supabase
          .from("session")
          .select("id, start_ts, sport_id, name, duration_min")
          .eq("user_id", userId)
          .gte("start_ts", startIso)
          .lt("start_ts", endIso),
        supabase
          .from("meal")
          .select("id, ts, name, kcal")
          .eq("user_id", userId)
          .gte("ts", startIso)
          .lt("ts", endIso),
        supabase
          .from("body_measurement")
          .select("id, ts, weight_kg, body_fat_pct")
          .eq("user_id", userId)
          .gte("ts", startIso)
          .lt("ts", endIso),
        supabase
          .from("supplement_intake")
          .select("id, taken_at, supplement:supplement_id (name, unit, dose)")
          .eq("user_id", userId)
          .gte("taken_at", startIso)
          .lt("taken_at", endIso),
        supabase
          .from("recovery_session")
          .select("id, ts, kind, duration_min")
          .eq("user_id", userId)
          .gte("ts", startIso)
          .lt("ts", endIso),
        supabase
          .from("subjective_check_in")
          .select("id, ts, prompt_kind, mood, energy")
          .eq("user_id", userId)
          .gte("ts", startIso)
          .lt("ts", endIso),
        supabase
          .from("project")
          .select("id, name, end_date, status")
          .eq("user_id", userId)
          .gte("end_date", startDate)
          .lt("end_date", endDate),
      ]);

      const events: CalendarEvent[] = [];
      const userEvents = (usr.data ?? []) as unknown as Array<{
        id: string;
        starts_at: string;
        ends_at: string;
        title: string | null;
        location: string | null;
        all_day: boolean;
        source: string | null;
      }>;
      for (const e of userEvents)
        events.push({
          id: `usr:${e.id}`,
          date: e.starts_at.slice(0, 10),
          ts: e.starts_at,
          kind: "user_event",
          title: e.title ?? "Event",
          detail: e.location ?? (e.all_day ? "all day" : undefined),
        });
      for (const e of ext.data ?? [])
        events.push({
          id: `ext:${e.id}`,
          date: e.starts_at.slice(0, 10),
          ts: e.starts_at,
          kind: "external",
          title: `${sourceLabel(e.source)} · ${e.title ?? "Event"}`,
          detail: e.location ?? (e.all_day ? "all day" : undefined),
        });
      for (const s of sess.data ?? [])
        events.push({
          id: `session:${s.id}`,
          date: s.start_ts.slice(0, 10),
          ts: s.start_ts,
          kind: "session",
          title: s.name || s.sport_id.replace(/_/g, " "),
          detail: `${Math.round(Number(s.duration_min))}min`,
        });
      for (const m of meals.data ?? [])
        events.push({
          id: `meal:${m.id}`,
          date: m.ts.slice(0, 10),
          ts: m.ts,
          kind: "meal",
          title: m.name || "Meal",
          detail: m.kcal ? `${Math.round(Number(m.kcal))} kcal` : undefined,
        });
      for (const b of body.data ?? [])
        events.push({
          id: `body:${b.id}`,
          date: b.ts.slice(0, 10),
          ts: b.ts,
          kind: "body",
          title: b.weight_kg ? `${Number(b.weight_kg).toFixed(1)} kg` : "Measurement",
          detail: b.body_fat_pct ? `${Number(b.body_fat_pct).toFixed(1)}% BF` : undefined,
        });
      for (const i of supp.data ?? []) {
        const row = i as unknown as {
          id: string;
          taken_at: string;
          supplement: { name: string; unit: string; dose: number | null } | { name: string; unit: string; dose: number | null }[] | null;
        };
        const sRel = Array.isArray(row.supplement) ? row.supplement[0] : row.supplement;
        events.push({
          id: `supp:${row.id}`,
          date: row.taken_at.slice(0, 10),
          ts: row.taken_at,
          kind: "supplement",
          title: sRel?.name ?? "Supplement",
          detail: sRel?.dose != null ? `${Number(sRel.dose)}${sRel.unit}` : undefined,
        });
      }
      for (const r of rec.data ?? [])
        events.push({
          id: `rec:${r.id}`,
          date: r.ts.slice(0, 10),
          ts: r.ts,
          kind: "recovery",
          title: r.kind.replace(/_/g, " "),
          detail: r.duration_min ? `${r.duration_min}min` : undefined,
        });
      for (const c of sub.data ?? [])
        events.push({
          id: `mood:${c.id}`,
          date: c.ts.slice(0, 10),
          ts: c.ts,
          kind: "mood",
          title: c.mood ? `Mood: ${c.mood}` : `Check-in (${c.prompt_kind})`,
          detail: c.energy != null ? `Energy ${c.energy}` : undefined,
        });
      for (const p of proj.data ?? []) {
        if (!p.end_date) continue;
        events.push({
          id: `proj:${p.id}`,
          date: p.end_date,
          kind: "project_deadline",
          title: `${p.name} · deadline`,
          detail: p.status,
        });
      }
      return events;
    },
  });
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function sourceLabel(s: string): string {
  switch (s) {
    case "google": return "Google";
    case "icloud": return "iCloud";
    case "outlook": return "Outlook";
    case "caldav": return "Calendar";
    default: return s;
  }
}
